import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useRef, useState } from "react";
import { Link, useLocation } from "react-router";
import { ROLE_LABELS, type MeResponse } from "@klantpunt/shared";
import { can, meKey } from "../lib/auth";
import { ORG_PATH } from "./navItems";
import { THEME_LABEL, type ThemeChoice } from "../lib/theme";
import {
  IconCheck,
  IconChevronDown,
  IconHelp,
  IconInbox,
  IconLogout,
  IconMonitor,
  IconMoon,
  IconSun,
  IconPlatform,
  IconRefresh,
  IconSettings,
  IconSwitch,
  IconUser,
} from "./icons";
import { useSwitchOrg } from "./useSwitchOrg";
import { Segmented } from "./Choice";
import { NewDot } from "../features/helpcenter/NewDot";
import { useToast } from "../lib/toast";
import { APP_COMMIT, APP_VERSION, reloadToNewVersion, useNewVersionAvailable } from "../lib/version";

/** Initialen voor de avatar: uit de naam, anders uit het e-mailadres. */
export function initials(user: { name: string | null; email: string }) {
  const src = (user.name ?? user.email.split("@")[0] ?? "").trim();
  const parts = src.split(/[\s._-]+/).filter(Boolean);
  const s = parts.length > 1 ? parts[0]![0]! + parts[parts.length - 1]![0]! : src.slice(0, 2);
  return s.toUpperCase();
}

export function Avatar({ user, size = "md" }: { user: { name: string | null; email: string }; size?: "md" | "lg" }) {
  return (
    <span className={`avatar ${size}`} aria-hidden="true">
      {initials(user)}
    </span>
  );
}

const THEMES: { k: ThemeChoice; Icon: typeof IconSun }[] = [
  { k: "system", Icon: IconMonitor },
  { k: "dark", Icon: IconMoon },
  { k: "light", Icon: IconSun },
];

/** Keuze Systeem / Donker / Licht als segment (radiogroep). */
export function ThemeSwitch({ theme, setTheme }: { theme: ThemeChoice; setTheme: (t: ThemeChoice) => void }) {
  return (
    <Segmented
      radio
      label="Thema"
      items={THEMES.map(({ k, Icon }) => ({
        value: k,
        label: THEME_LABEL[k],
        icon: <Icon size={16} aria-hidden="true" />,
      }))}
      value={theme}
      onChange={setTheme}
    />
  );
}

/** Avatar rechtsboven met profiel, organisatie wisselen, thema en uitloggen. */
export function ProfileMenu({
  me,
  theme,
  setTheme,
  onLogout,
}: {
  me: MeResponse;
  theme: ThemeChoice;
  setTheme: (t: ThemeChoice) => void;
  onLogout: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const id = useId();
  const loc = useLocation();
  const switchOrg = useSwitchOrg();
  const qc = useQueryClient();
  const toast = useToast();
  // Bij openen vergelijken met wat er live staat: een vastgehouden oude versie (PWA) ziet dan dat er een nieuwere is.
  const newVersion = useNewVersionAvailable(open);

  // Bij openen verversen: een nieuwe uitnodiging (andere organisatie) staat dan meteen in de lijst.
  useEffect(() => {
    if (open) void qc.invalidateQueries({ queryKey: meKey });
  }, [open, qc]);

  useEffect(() => setOpen(false), [loc.pathname]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        btn.current?.focus();
      }
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="profilemenu" ref={ref}>
      <button
        ref={btn}
        type="button"
        className="avatarbtn"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={id}
        aria-label="Profielmenu"
        onClick={() => setOpen(!open)}
      >
        <Avatar user={me.user} />
        <IconChevronDown size={18} aria-hidden="true" className="hide-mobile" />
      </button>
      {open && (
        <div className="popover" id={id} role="dialog" aria-label="Profiel">
          <div className="pm-head">
            <Avatar user={me.user} size="lg" />
            <span className="pm-who">
              {me.user.name ? (
                <strong>{me.user.name}</strong>
              ) : (
                <strong className="pm-email" title={me.user.email}>
                  {me.user.email}
                </strong>
              )}
              {me.user.name && (
                <span className="muted pm-email" title={me.user.email}>
                  {me.user.email}
                </span>
              )}
              {me.role && me.org && (
                <span className="muted small">
                  {ROLE_LABELS[me.role]} · {me.org.name}
                </span>
              )}
            </span>
          </div>
          <div className="pm-sep" />
          <Link className="menuitem" to="/profiel">
            <IconUser size={20} aria-hidden="true" />
            Profiel en instellingen
          </Link>
          {can(me, "users.manage") && (
            <Link className="menuitem" to={ORG_PATH}>
              <IconSettings size={20} aria-hidden="true" />
              Organisatie
            </Link>
          )}
          {me.orgs.length > 1 && (
            <div className="pm-orgs">
              <span className="muted small" id={`${id}-orgs`}>
                Organisatie wisselen
              </span>
              <div role="radiogroup" aria-labelledby={`${id}-orgs`}>
                {me.orgs.map((o) => {
                  const current = o.id === me.org?.id;
                  return (
                    <button
                      key={o.id}
                      type="button"
                      role="radio"
                      aria-checked={current}
                      className="menuitem orgitem"
                      onClick={() => {
                        setOpen(false);
                        if (!current) void switchOrg(o.id);
                      }}
                    >
                      {current ? (
                        <IconCheck size={20} aria-hidden="true" />
                      ) : (
                        <IconSwitch size={20} aria-hidden="true" />
                      )}
                      <span className="grow">
                        {o.name}
                        <span className="muted small block">{ROLE_LABELS[o.role]}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <Link className="menuitem" to={`/hulp?vanaf=${encodeURIComponent(loc.pathname + loc.search)}`}>
            <IconHelp size={20} aria-hidden="true" />
            Hulp en support
            <NewDot />
          </Link>
          {me.support && (
            <Link className="menuitem" to="/platform/support">
              <IconInbox size={20} aria-hidden="true" />
              Supportinbox
            </Link>
          )}
          {me.user.platformAdmin && (
            <Link className="menuitem" to="/platform">
              <IconPlatform size={20} aria-hidden="true" />
              Platformbeheer
            </Link>
          )}
          <div className="pm-theme">
            <span className="muted small">Thema</span>
            <ThemeSwitch theme={theme} setTheme={setTheme} />
          </div>
          <div className="pm-sep" />
          {newVersion && (
            <button type="button" className="menuitem" onClick={() => void reloadToNewVersion()}>
              <IconRefresh size={20} aria-hidden="true" />
              <span className="grow">
                Nieuwe versie beschikbaar
                <span className="muted small block">Vernieuwen</span>
              </span>
            </button>
          )}
          <button type="button" className="menuitem" onClick={onLogout}>
            <IconLogout size={20} aria-hidden="true" />
            Uitloggen
          </button>
          <button
            type="button"
            className="pm-version"
            title={APP_COMMIT ? "Kopieer de volledige versie" : undefined}
            onClick={() => {
              if (!APP_COMMIT) return;
              void navigator.clipboard
                ?.writeText(`${APP_VERSION} (${APP_COMMIT})`)
                .then(() => toast("Versie gekopieerd"))
                .catch(() => {});
            }}
          >
            Versie {APP_VERSION}
          </button>
        </div>
      )}
    </div>
  );
}
